import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { UserPlus, Users, Search, X, Check, Building } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PresentielManager = ({ assemblee, token, onParticipantAdded }) => {
  const [showPanel, setShowPanel] = useState(false);
  const [availableCopros, setAvailableCopros] = useState([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState(null);

  const fetchAvailableCopros = async () => {
    if (!assemblee?.id || !token) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(
        `${API}/assemblees/${assemblee.id}/available-coproprietaires`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAvailableCopros(response.data);
    } catch (err) {
      console.error('Error fetching available copros:', err);
      setError('Erreur lors du chargement des coproprietaires');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (showPanel) {
      fetchAvailableCopros();
    }
  }, [showPanel, assemblee?.id, token]);

  const addPresentielParticipant = async (userId) => {
    setAdding(userId);
    setError(null);
    
    try {
      const response = await axios.post(
        `${API}/assemblees/${assemblee.id}/add-presentiel-participant`,
        { user_id: userId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Remove from available list
      setAvailableCopros(prev => prev.filter(c => c.id !== userId));
      
      // Notify parent
      if (onParticipantAdded) {
        onParticipantAdded(response.data);
      }
      
    } catch (err) {
      console.error('Error adding participant:', err);
      setError(err.response?.data?.detail || 'Erreur lors de l\'ajout du participant');
    } finally {
      setAdding(null);
    }
  };

  // Filter copros by search term
  const filteredCopros = availableCopros.filter(c => {
    const fullName = `${c.prenom} ${c.nom}`.toLowerCase();
    const searchLower = searchTerm.toLowerCase();
    return fullName.includes(searchLower) || 
           c.email?.toLowerCase().includes(searchLower) ||
           c.lot?.toLowerCase().includes(searchLower);
  });

  return (
    <div className="relative">
      {/* Toggle Button */}
      <button
        onClick={() => setShowPanel(!showPanel)}
        className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-all ${
          showPanel 
            ? 'bg-purple-600 text-white' 
            : 'border-2 border-purple-500 text-purple-600 hover:bg-purple-50'
        }`}
        data-testid="add-presentiel-btn"
      >
        <UserPlus size={18} />
        <span>Ajouter présentiel</span>
      </button>

      {/* Dropdown Panel - aligned to right to prevent overflow */}
      {showPanel && (
        <div className="absolute top-full right-0 mt-2 w-96 bg-white rounded-xl shadow-xl border border-gray-200 z-50">
          <div className="p-4 border-b border-gray-100">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-800 flex items-center space-x-2">
                <Building size={18} className="text-purple-500" />
                <span>Copropriétaires présents en salle</span>
              </h3>
              <button
                onClick={() => setShowPanel(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={18} />
              </button>
            </div>
            
            {/* Search */}
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Rechercher un coproprietaire..."
                className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-purple-400"
                data-testid="search-copro-input"
              />
            </div>
          </div>

          {/* Error message */}
          {error && (
            <div className="px-4 py-2 bg-red-50 text-red-700 text-sm">
              {error}
            </div>
          )}

          {/* List */}
          <div className="max-h-64 overflow-y-auto">
            {loading ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500 mx-auto mb-2"></div>
                <p className="text-sm text-gray-500">Chargement...</p>
              </div>
            ) : filteredCopros.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <Users className="mx-auto mb-2 text-gray-300" size={32} />
                <p className="text-sm">
                  {searchTerm 
                    ? 'Aucun coproprietaire trouvé' 
                    : 'Tous les coproprietaires participent déjà'}
                </p>
              </div>
            ) : (
              filteredCopros.map((copro) => (
                <div 
                  key={copro.id}
                  className="flex items-center justify-between px-4 py-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-800 truncate">
                      {copro.prenom} {copro.nom}
                    </p>
                    <p className="text-xs text-gray-500 truncate">
                      {copro.lot && `Lot ${copro.lot} - `}
                      {copro.tantiemes > 0 && `${copro.tantiemes} tantièmes`}
                    </p>
                  </div>
                  
                  <button
                    onClick={() => addPresentielParticipant(copro.id)}
                    disabled={adding === copro.id}
                    className="ml-3 flex items-center space-x-1 px-3 py-1.5 bg-purple-500 hover:bg-purple-600 text-white rounded-lg text-sm font-medium disabled:opacity-50 transition-colors"
                    data-testid={`add-copro-${copro.id}`}
                  >
                    {adding === copro.id ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                    ) : (
                      <>
                        <Check size={14} />
                        <span>Présent</span>
                      </>
                    )}
                  </button>
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          <div className="px-4 py-3 bg-gray-50 rounded-b-xl border-t border-gray-100">
            <p className="text-xs text-gray-500 text-center">
              {filteredCopros.length} coproprietaire(s) non inscrit(s)
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default PresentielManager;
